import { ArrowDownToLine, ArrowUpFromLine, Check, Copy, Eye, EyeOff, X } from "lucide-react";
import { cloneElement, useState, type FormEvent, type ReactElement } from "react";
import { useAuth } from "../auth/AuthContext";
import { AutofillUsername } from "./AutofillUsername";
import { ApiError, apiRequest } from "../lib/api";
import type { ApiEnvelope, DepositLimit, FundingInstructions, User, WalletTransaction } from "../types";
import { money } from "../lib/format";
import { Modal } from "./Modal";
import { ButtonLabel } from "./ButtonLabel";

const PRESETS = [500, 1000, 2500, 5000];

interface Props {
  walletId: string;
  type: "deposit" | "withdrawal";
  maxAmount?: number;
  /** Kuspit's monthly cap while the file is incomplete; deposits over what remains are refused before they are sent. */
  depositLimit?: DepositLimit | null;
  onCompleted(transaction: WalletTransaction): void;
  trigger: ReactElement<{ onClick?: () => void }>;
}

export function OperationDialog({ walletId, type, maxAmount, depositLimit, onCompleted, trigger }: Props) {
  const { user, applyUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Live mode has no deposit instruction — funding is an inbound SPEI to the wallet's CLABE. When
  // the API says so (DEPOSIT_NOT_INSTRUCTABLE), the dialog pivots to showing that CLABE instead of
  // reporting a failure, so "Agregar fondos" is useful whether the connector is mock or live.
  const [funding, setFunding] = useState<(FundingInstructions & { message: string }) | null>(null);
  const [copied, setCopied] = useState(false);
  const isDeposit = type === "deposit";
  // An account created through Google or Apple has no password yet, and step-up requires one.
  // Rather than confirm a password the user never set, the first operation creates it.
  const needsPassword = user?.hasPassword === false;

  const reset = () => {
    setAmount(""); setPassword(""); setError(null); setShowPassword(false);
    setFunding(null); setCopied(false);
  };

  const copyClabe = async () => {
    if (!funding) return;
    try {
      await navigator.clipboard.writeText(funding.clabe);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* the CLABE is selectable on screen as a fallback */ }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) { setError("Ingresa un monto válido."); return; }
    if (!isDeposit && maxAmount !== undefined && numericAmount > maxAmount) { setError("El monto supera tu saldo disponible."); return; }
    if (isDeposit && depositLimit?.applies && numericAmount > depositLimit.remainingMxn) {
      setError(depositLimit.remainingMxn > 0
        ? `Mientras Kuspit completa tu expediente, este mes puedes aportar hasta ${money.format(depositLimit.remainingMxn)} más.`
        : `Este mes ya alcanzaste el límite de ${money.format(depositLimit.limitMxn)} que Kuspit permite mientras tu expediente está incompleto.`);
      return;
    }
    setSubmitting(true);
    try {
      if (needsPassword) {
        const created = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/auth/password/set", {
          method: "POST", body: JSON.stringify({ password })
        });
        applyUser(created.data.user);
      }
      const stepUp = await apiRequest<ApiEnvelope<{ stepUpToken: string }>>("/api/v1/auth/step-up", {
        method: "POST", body: JSON.stringify({ password })
      });
      const result = await apiRequest<ApiEnvelope<{ transaction: WalletTransaction }>>(
        `/api/v1/wallets/${walletId}/${isDeposit ? "deposits" : "withdrawals"}`,
        {
          method: "POST",
          headers: {
            "idempotency-key": crypto.randomUUID(),
            "x-step-up-token": stepUp.data.stepUpToken
          },
          body: JSON.stringify({ amount: numericAmount })
        }
      );
      onCompleted(result.data.transaction);
      setOpen(false);
      reset();
    } catch (cause) {
      // Live mode: deposits are not instructable, they arrive by SPEI. Show the wallet's CLABE
      // rather than an error the customer cannot act on.
      if (isDeposit && cause instanceof ApiError && cause.code === "DEPOSIT_NOT_INSTRUCTABLE") {
        try {
          const payload = await apiRequest<ApiEnvelope<{ funding: FundingInstructions }>>(
            `/api/v1/wallets/${walletId}/funding-instructions`
          );
          setFunding({ ...payload.data.funding, message: cause.message });
        } catch (fundingCause) {
          setError(fundingCause instanceof Error ? fundingCause.message : cause.message);
        }
      } else {
        setError(cause instanceof Error ? cause.message : "No pudimos enviar la operación.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {cloneElement(trigger, { onClick: () => setOpen(true) })}
      <Modal open={open} label={isDeposit ? "Agregar fondos" : "Retirar fondos"} onOpenChange={(next) => { setOpen(next); if (!next) reset(); }}>
        <div className="dialog-content" aria-describedby="operation-description">
          <div className={`dialog-icon ${isDeposit ? "deposit" : "withdrawal"}`}>
            {isDeposit ? <ArrowDownToLine aria-hidden="true" /> : <ArrowUpFromLine aria-hidden="true" />}
          </div>
          <span className="page-eyebrow">Wallet Libre</span>
          <h2>{isDeposit ? "Agregar fondos" : "Retirar fondos"}</h2>
          <p id="operation-description">
            {isDeposit ? "Aportación en pesos mexicanos" : `Disponible: ${formatCurrency(maxAmount ?? 0)}`}
          </p>
          {funding ? (
            <div className="dialog-form">
              <div className="form-message" role="status">{funding.message}</div>
              <div className="field-block">
                <span className="field-label" id="op-funding-clabe-label">CLABE interbancaria</span>
                <div className="clabe-display" aria-labelledby="op-funding-clabe-label" translate="no">{funding.clabe}</div>
                {funding.beneficiaryName && (
                  <span className="field-hint">Beneficiario: {funding.beneficiaryName} · {funding.currency}</span>
                )}
              </div>
              <button type="button" className="primary-button full" onClick={() => void copyClabe()}>
                {copied ? <><Check size={17} aria-hidden="true" /> CLABE copiada</> : <><Copy size={17} aria-hidden="true" /> Copiar CLABE</>}
              </button>
              <p className="amount-hint">Tu aportación se reflejará en cuanto el banco confirme la transferencia SPEI.</p>
            </div>
          ) : (
          <form onSubmit={submit} className="dialog-form">
            <AutofillUsername />
            <div className="amount-display">
              <label className="field-label" htmlFor={`${type}-amount`}>Monto</label>
              <div className="money-field">
                <span aria-hidden="true">$</span>
                <input
                  id={`${type}-amount`}
                  name={`${type}-amount`}
                  inputMode="decimal"
                  // A real autocomplete token, not "off": a bare text input directly above a
                  // current-password field is exactly where a browser drops the saved e-mail, and
                  // browsers honour a typed token where they ignore "off".
                  autoComplete="transaction-amount"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0"
                  aria-describedby={`${type}-currency`}
                  autoFocus
                  required
                />
                <span id={`${type}-currency`}>MXN</span>
              </div>
              {isDeposit && <span className="amount-hint">Tu aportación queda disponible en cuanto se confirma.</span>}
              {isDeposit && depositLimit?.applies && (
                <span className="amount-hint" translate="no">Límite mensual mientras Kuspit completa tu expediente: te quedan {money.format(depositLimit.remainingMxn)} de {money.format(depositLimit.limitMxn)}.</span>
              )}
            </div>
            <div className="preset-grid" role="group" aria-label="Cantidades sugeridas">
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`preset-chip${Number(amount) === preset ? " active" : ""}`}
                  aria-pressed={Number(amount) === preset}
                  disabled={(!isDeposit && maxAmount !== undefined && preset > maxAmount) || (isDeposit && Boolean(depositLimit?.applies) && preset > (depositLimit?.remainingMxn ?? Infinity))}
                  onClick={() => setAmount(String(preset))}
                >
                  ${preset.toLocaleString("es-MX")}
                </button>
              ))}
            </div>
            <label htmlFor={`${type}-password`}>
              {needsPassword ? "Crea una contraseña para confirmar" : "Confirma tu contraseña"}
            </label>
            <div className="password-field">
              <input
                id={`${type}-password`}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={needsPassword ? "new-password" : "current-password"}
                aria-describedby={needsPassword ? `${type}-password-hint` : undefined}
                minLength={needsPassword ? 12 : undefined}
                required
              />
              <button type="button" className="field-icon" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                {showPassword ? <EyeOff size={19} aria-hidden="true" /> : <Eye size={19} aria-hidden="true" />}
              </button>
            </div>
            {needsPassword && (
              <span className="amount-hint" id={`${type}-password-hint`}>
                Tu cuenta se creó con Apple o Google. Las operaciones financieras necesitan una
                contraseña: 12 caracteres, mayúscula, minúscula, número y símbolo.
              </span>
            )}
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="primary-button full" type="submit" disabled={submitting}>
              <ButtonLabel busy={submitting} busyLabel="Enviando" label={isDeposit ? "Confirmar aportación" : "Confirmar retiro"} />
            </button>
          </form>
          )}
          <button type="button" className="dialog-close" onClick={() => setOpen(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        </div>
      </Modal>
    </>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);
}
